import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { message, Button, Dropdown, Menu, Tooltip, Spin, Input, Modal } from 'antd';
import ZoomVideo from '@zoom/videosdk';
import {
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  moderateText,
  logSensitiveKeywordEvent,
} from 'redux/actions/mvp/session.action';
import { fetchUserMvp } from 'redux/actions/user.action';
import { I18n } from '../../../../helpers/i18n';
import sensitiveKeywordsConfig from '../../../../config/sensitiveKeywords.json';
import './session-video-custom.scss';

/** Kiểm tra transcript có chứa từ khóa trong danh sách JSON không. Trả về { detected, category, keyword } hoặc { detected: false }. */
function detectSensitiveByJson(text) {
  if (!text || !sensitiveKeywordsConfig.categories || !Array.isArray(sensitiveKeywordsConfig.categories)) {
    return { detected: false };
  }
  const lower = text.toLowerCase().trim();
  for (const cat of sensitiveKeywordsConfig.categories) {
    const keywords = cat.keywords || [];
    for (const kw of keywords) {
      if (!kw || typeof kw !== 'string') continue;
      if (lower.includes(kw.toLowerCase())) {
        return { detected: true, category: cat.name, keyword: kw };
      }
    }
  }
  return { detected: false };
}

/** Bật lại (true) để xem console debug log của Live Transcript / từ khóa nhạy cảm. */
const DEBUG_SENSITIVE_KEYWORD = false;

import {
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  ShareAltOutlined,
  StopOutlined,
  MessageOutlined,
  PhoneOutlined,
  MoreOutlined,
  LoadingOutlined,
  UserOutlined,
  CloseOutlined,
  SendOutlined,
} from '@ant-design/icons';

function SessionVideoCustom({
  history,
  match,
  user,
  getDetailSession,
  getTokenSession,
  verifyJoinSession,
  moderateText: moderateTextAction,
  logSensitiveKeywordEvent: logSensitiveKeywordEventAction,
}) {
  const sessionId = match.params.id;
  const [client, setClient] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const userVideoRefs = useRef({});
  const shareCanvasRef = useRef(null);
  const shareVideoRef = useRef(null);
  const [shareRenderType, setShareRenderType] = useState('canvas'); // 'canvas' | 'video'
  const [activeShareUser, setActiveShareUser] = useState(null);
  const [pinnedUser, setPinnedUser] = useState(null);
  const [showUserList, setShowUserList] = useState(false); // Control user list panel visibility
  const [showChat, setShowChat] = useState(false); // Control chat panel visibility
  const [chatMessages, setChatMessages] = useState([]); // Zoom chat messages
  const [chatInput, setChatInput] = useState('');
  const [chatClient, setChatClient] = useState(null);
  const [chatToast, setChatToast] = useState(null); // { sender, text }
  /** Modal cảnh báo từ khóa nhạy cảm: { keyword, category, speaker, transcript, source } khi mở, null khi đóng */
  const [sensitiveModal, setSensitiveModal] = useState(null);
  const [sensitiveModalSubmitting, setSensitiveModalSubmitting] = useState(false);
  /** Flow dừng cuộc gọi: sau khi chọn "Dừng cuộc gọi" mở modal nhập feedback, lưu data để gọi API khi confirm */
  const [endSessionFeedbackVisible, setEndSessionFeedbackVisible] = useState(false);
  const [endSessionFeedbackNote, setEndSessionFeedbackNote] = useState('');
  const pendingEndSessionRef = useRef(null);

  const pinnedUserRef = useRef(null);
  const activeShareUserRef = useRef(null);
  const chatToastTimeoutRef = useRef(null);
  const sessionUserIdRef = useRef(null);
  const sensitiveCheckInFlightRef = useRef(false);
  /** Tránh join nhiều lần (Strict Mode / re-mount) và cleanup khi unmount */
  const joiningInProgressRef = useRef(false);
  const mountedRef = useRef(true);
  const clientRef = useRef(null);

  // Video resolution constant
  const RESOLUTION = { width: 640, height: 360 };

  useEffect(() => {
    pinnedUserRef.current = pinnedUser;
  }, [pinnedUser]);

  useEffect(() => {
    activeShareUserRef.current = activeShareUser;
  }, [activeShareUser]);

  const joinSession = async () => {
    if (joiningInProgressRef.current || clientRef.current) return;
    joiningInProgressRef.current = true;
    setLoading(true);
    try {
      const sessionInfo = await getDetailSession({ sessionId });
      if (!sessionInfo) throw new Error('No session info');

      const verifyData = await verifyJoinSession({
        sessionId: sessionInfo.sessionUserId,
        sessionUserId: sessionInfo.sessionUserId,
      });
      if (!verifyData || verifyData.status !== 'OK') {
        throw new Error('Unable to join session. Please check your permission or session status.');
      }

      let infoZoom = verifyData.infoZoom;
      if (typeof infoZoom === 'string') infoZoom = JSON.parse(infoZoom);
      if (!infoZoom) throw new Error('No Zoom info');

      const tokenData = await getTokenSession({ sessionName: infoZoom.sessionName });
      const token = typeof tokenData === 'string' ? tokenData : tokenData?.token || tokenData?.data;
      if (!token) throw new Error('No token');
      const userName = user?.name || 'Anonymous User';
      const sessionPasscode = infoZoom.password || '';

      const client = ZoomVideo.createClient();
      await client.init('en-US', 'Global');
      // Leave any existing session to prevent "duplicated operation" error
      client.leave();
      await client.join(infoZoom.sessionName, token, userName, sessionPasscode);
      if (!mountedRef.current) {
        try {
          await client.leave();
        } catch (e) {
          console.warn('Leave on unmount:', e);
        }
        return;
      }
      sessionUserIdRef.current = sessionInfo.sessionUserId;
      clientRef.current = client;
      setClient(client);

      // Log cấu hình từ nhạy cảm để debug
      if (DEBUG_SENSITIVE_KEYWORD) {
        const categoriesCount = sensitiveKeywordsConfig.categories?.length ?? 0;
        const keywordsCount = sensitiveKeywordsConfig.categories?.reduce(
          (sum, c) => sum + (c.keywords?.length ?? 0),
          0
        ) ?? 0;
        console.log('[Sensitive keyword] Cấu hình đã load:', {
          categoriesCount,
          keywordsCount,
          categories: sensitiveKeywordsConfig.categories?.map((c) => c.name) ?? [],
        });
        console.log(
          "[Live Transcript] Đã đăng ký listener 'caption-message'. Cần bật Live Transcription trong phiên Zoom để nhận transcript."
        );
      }
      // Debug: lắng nghe thêm 'transcription-text' nếu SDK dùng tên khác (chỉ log khi DEBUG_SENSITIVE_KEYWORD)
      if (DEBUG_SENSITIVE_KEYWORD && typeof client.on === 'function') {
        try {
          client.on('transcription-text', (p) => {
            console.log('[Live Transcript] [DEBUG] Event transcription-text nhận được:', p ? { keys: Object.keys(p), text: p.text ?? p.message ?? p.content } : p);
          });
        } catch (e) {
          // event có thể không tồn tại
        }
      }
      const handleTranscriptPayload = (eventName, payload) => {
        const payloadKeys = payload ? Object.keys(payload) : [];
        const textRaw = payload && (payload.text ?? payload.message ?? payload.content ?? payload.transcript ?? payload.messageContent);
        const text = textRaw != null ? String(textRaw).trim() : '';
        const speakerName = (payload && (payload.displayName || payload.userName || payload.speakerName || payload.speaker)) || 'Unknown';
        const inFlight = sensitiveCheckInFlightRef.current;

        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive NLP] Caption nhận được:', {
            event: eventName,
            speaker: speakerName,
            textLength: text.length,
            textPreview: text ? text.substring(0, 60) + (text.length > 60 ? '...' : '') : '(rỗng)',
            inFlight,
          });
        }
        if (DEBUG_SENSITIVE_KEYWORD) {
          if (eventName !== 'caption-message') {
            console.log('[Live Transcript] Alternative event:', eventName, payload ? Object.keys(payload) : []);
          }
          console.log('[Live Transcript] caption-message event:', {
            payloadKeys,
            speaker: speakerName,
            text: text ? text.substring(0, 100) : '',
            textLength: text.length,
            inFlight,
          });
        }

        if (!text || inFlight) {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Live Transcript] Bỏ qua (không xử lý):', {
              reason: !text ? 'text rỗng' : 'đang xử lý caption trước (inFlight=true)',
            });
          }
          return;
        }

        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Live Transcript] Đang xử lý caption:', { speaker: speakerName, text: text.substring(0, 80) });
        }
        sensitiveCheckInFlightRef.current = true;

        // Bước 1: Kiểm tra theo danh sách từ khóa JSON
        const jsonResult = detectSensitiveByJson(text);
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive keyword] Kiểm tra JSON:', {
            transcript: text,
            jsonDetected: jsonResult.detected,
            ...(jsonResult.detected && { category: jsonResult.category, matchedKeyword: jsonResult.keyword }),
          });
          if (jsonResult.detected) {
            console.log('[Sensitive keyword] Phát hiện (nguồn: JSON):', {
              source: 'json',
              category: jsonResult.category,
              keyword: jsonResult.keyword,
              speaker: speakerName,
              transcript: text,
            });
          }
        }

        const showSensitiveModal = (payload) => {
          const modalState = {
            keyword: payload.keyword || payload.category || 'sensitive',
            category: payload.category || 'sensitive',
            speaker: payload.speakerName,
            transcript: payload.text,
            source: payload.source,
          };
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Sensitive keyword] Hiển thị modal kiểm duyệt.', { source: payload.source, category: modalState.category });
          }
          try {
            const stream = client.getMediaStream();
            const currentUserId = client.getCurrentUserInfo()?.userId;
            if (stream) {
              stream.muteAudio().catch(() => {});
              stream.stopVideo().catch(() => {});
              if (currentUserId) {
                try {
                  stream.detachVideo(currentUserId);
                } catch (detachErr) {
                  // ignore
                }
              }
            }
            setIsAudioEnabled(false);
            setIsVideoEnabled(false);
          } catch (e) {
            if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Sensitive keyword] Không tạm dừng được media:', e);
          }
          setSensitiveModal(modalState);
        };

        // Phát hiện từ JSON → hiển thị modal ngay, không chờ Google API (tránh lỗi API làm modal không hiện)
        if (jsonResult.detected) {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.warn('[Sensitive keyword] Hiển thị modal ngay (nguồn: JSON)');
          }
          showSensitiveModal({
            keyword: jsonResult.keyword,
            category: jsonResult.category,
            speakerName,
            text,
            source: 'json',
          });
          sensitiveCheckInFlightRef.current = false;
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Xong xử lý caption (sẵn sàng nhận caption tiếp theo).');
          return;
        }

        // Chỉ khi JSON không phát hiện mới gọi Google NLP
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Sensitive keyword] Gọi API moderate-text (Google NLP)...', { text: text.substring(0, 80) });
        }
        const thunk = moderateTextAction && moderateTextAction({ text });
        const promise = typeof thunk === 'function' ? thunk() : thunk;
        (promise || Promise.resolve(null))
          .then((res) => {
            // Chuẩn hóa response: client có thể trả res hoặc res.data tùy cách wrap
            const payload = res && (res.data !== undefined ? res.data : res);
            const googleDetected = payload && payload.detected === true;
            if (DEBUG_SENSITIVE_KEYWORD) {
              console.log('[Sensitive keyword] Google NLP kết quả:', {
                detected: payload ? payload.detected : false,
                category: payload ? payload.category : null,
                confidence: payload ? payload.confidence : null,
                allCategories: payload ? payload.allCategories : null,
              });
              if (payload && payload.detected) {
                console.log('[Sensitive keyword] Phát hiện (nguồn: Google NLP):', {
                  source: 'google',
                  category: payload.category || 'sensitive',
                  confidence: payload.confidence,
                  speaker: speakerName,
                  transcript: text,
                });
              }
            }
            if (googleDetected) {
              if (DEBUG_SENSITIVE_KEYWORD) {
                console.warn('[Sensitive keyword] Hiển thị modal (nguồn: Google NLP)');
              }
              showSensitiveModal({
                keyword: payload.keyword || null,
                category: payload.category || 'sensitive',
                speakerName,
                text,
                source: 'google',
              });
            } else if (DEBUG_SENSITIVE_KEYWORD) {
                console.log('[Sensitive keyword] Tổng kết: không phát hiện từ nhạy cảm', {
                  byGoogle: false,
                  textPreview: text.substring(0, 50),
                });
              }
          })
          .catch((err) => {
            if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Sensitive keyword] Lỗi gọi moderate-text:', err && (err.message || err.response || err));
          })
          .finally(() => {
            sensitiveCheckInFlightRef.current = false;
            if (DEBUG_SENSITIVE_KEYWORD) console.log('[Sensitive keyword] Xong xử lý caption (sẵn sàng nhận caption tiếp theo).');
          });
      };

      client.on('caption-message', (payload) => handleTranscriptPayload('caption-message', payload));
      try {
        client.on('live-transcription-message-received', (payload) => handleTranscriptPayload('live-transcription-message-received', payload));
      } catch (e) {
        // Zoom SDK có thể không có event này
      }

      // Tự động thử bật Live Transcription khi bất kỳ ai (Explorer hoặc Guide) join phiên — ai vào trước cũng thử bật để kiểm duyệt từ khóa sớm (Zoom chỉ cho phép host bật; nếu không phải host sẽ lỗi 7300, log thân thiện)
      try {
        const hasLtt = !!(client.getLiveTranscriptionClient && typeof client.getLiveTranscriptionClient === 'function');
        if (DEBUG_SENSITIVE_KEYWORD) {
          console.log('[Live Transcript] getLiveTranscriptionClient:', hasLtt ? 'có' : 'không có (SDK có thể không hỗ trợ hoặc phiên bản cũ)');
        }
        const lttClient = hasLtt ? client.getLiveTranscriptionClient() : null;
        if (!lttClient) {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] SDK không có getLiveTranscriptionClient — cần host bật transcription trong Zoom.');
        } else if (typeof lttClient.startLiveTranscription !== 'function') {
          if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Live Transcript] startLiveTranscription không khả dụng. Các method có sẵn:', typeof lttClient === 'object' ? Object.keys(lttClient).filter((k) => typeof lttClient[k] === 'function') : []);
          }
        } else {
          if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] Đang thử bật Live Transcript (startLiveTranscription)...');
          const result = lttClient.startLiveTranscription();
          if (result && typeof result.then === 'function') {
            result
              .then(() => { if (DEBUG_SENSITIVE_KEYWORD) console.log('[Live Transcript] Đã bật Live Transcript thành công.'); })
              .catch((e) => {
                if (!DEBUG_SENSITIVE_KEYWORD) return;
                const code = e && (e.errorCode ?? e.code);
                const reason = (e && (e.reason || e.message)) || '';
                const is7300 =
                  code === 7300 ||
                  (e && e.type === 'INVALID_OPERATION') ||
                  (typeof reason === 'string' && reason.toLowerCase().includes('live transcription is not enabled'));
                if (is7300) {
                  console.log(
                    '[Live Transcript] Live Transcript chưa bật (có thể cần host bật). Bật add-on Live Transcription cho tài khoản Zoom hoặc để Guide (host) bật trong menu Zoom.'
                  );
                } else {
                  console.warn('[Live Transcript] Không bật được:', e);
                }
              });
          } else if (DEBUG_SENSITIVE_KEYWORD) {
            console.log('[Live Transcript] Đã gọi startLiveTranscription (sync).');
          }
        }
      } catch (e) {
        if (DEBUG_SENSITIVE_KEYWORD) console.warn('[Live Transcript] Lỗi khi bật:', e);
      }

      // Init chat client after join
      try {
        const chat = client.getChatClient();
        setChatClient(chat);

        // Optionally load existing chat history
        try {
          const history = await chat.getHistory();
          if (Array.isArray(history)) {
            const currentUserId = client.getCurrentUserInfo()?.userId;
            const mapped = history
              .filter((item) => item && (item.message || item.content))
              .map((item, idx) => ({
                id: item.id || `${item.timestamp || ''}-${idx}`,
                sender:
                  item.sender?.displayName ||
                  item.sender?.userName ||
                  item.sender?.userId ||
                  'Unknown',
                text: item.message || item.content || '',
                timestamp: item.timestamp || new Date().toISOString(),
                isSelf: item.sender?.userId === currentUserId,
              }));
            setChatMessages(mapped);
          }
        } catch (e) {
          console.warn('Error getting history:', e);
        }
      } catch (e) {
        console.warn('Chat client not available:', e);
      }

      // Start local media (video & audio)
      const stream = client.getMediaStream();
      try {
        await stream.startVideo();
      } catch (e) {
        setIsVideoEnabled(false);
      }

      try {
        await stream.startAudio();
      } catch (e) {
        setIsAudioEnabled(false);
      }

      const users = client.getAllUser();
      setAllUsers(users);

      users.forEach((user) => {
        if (user.sharerOn) {
          setActiveShareUser(user);
          if (shareCanvasRef.current) {
            stream.startShareView(shareCanvasRef.current, user.userId);
          }
        }
      });

      // Listen for remote user join and video events
      client.on('user-added', (payload) => {
        // const currentUserId = client.getCurrentUserInfo().userId;
        // payload.forEach((item) => {
        //   if (item.userId !== currentUserId) message.info(`${item.displayName} joined the session.`);
        // });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-removed', (payload) => {
        payload.forEach((item) => {
          // message.info(`${item.displayName} left the session.`);

          if (pinnedUserRef.current && pinnedUserRef.current.userId === item.userId) {
            setPinnedUser(null);
          }

          if (activeShareUserRef.current && activeShareUserRef.current.userId === item.userId) {
            setActiveShareUser(null);
            setShareRenderType('canvas');
            try {
              const stream = client.getMediaStream();
              stream.stopShareView();
            } catch (e) {
              console.warn('Could not stop share view:', e);
            }
          }
        });
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('user-updated', (payload) => {
        const users = client.getAllUser();
        setAllUsers(users);
      });

      client.on('passively-stop-share', (payload) => {
        setActiveShareUser(null);
        setShareRenderType('canvas');
      });
    } catch (e) {
      if (mountedRef.current) {
        message.error(e.message || 'Failed to join session');
        history.push('/');
      }
    } finally {
      joiningInProgressRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  // Tự động join session khi vào trang (chỉ 1 lần; cleanup khi unmount để tránh duplicate join)
  useEffect(() => {
    mountedRef.current = true;
    joinSession();
    return () => {
      mountedRef.current = false;
      joiningInProgressRef.current = false;
      if (clientRef.current) {
        clientRef.current.leave().catch(() => {});
        clientRef.current = null;
      }
      setClient(null);
    };
  }, []);

  // Rời session
  const leaveSession = async () => {
    if (client) {
      await client.leave();
      clientRef.current = null;
      setClient(null);
      history.push(`/session-detail/${sessionId}`);
    }
  };

  // Bật/tắt audio
  const toggleAudio = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const currentUserId = client.getCurrentUserInfo().userId;

    if (isAudioEnabled) {
      try {
        await stream.muteAudio();
        setIsAudioEnabled(false);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: true } : user)),
        );
      } catch (e) {
        console.warn('Could not mute audio:', e);
        message.warning('Could not mute audio. Please check your microphone permissions.');
      }
    } else {
      try {
        await stream.unmuteAudio();
        setIsAudioEnabled(true);
        // Update user's muted status in allUsers
        setAllUsers((prevUsers) =>
          prevUsers.map((user) => (user.userId === currentUserId ? { ...user, muted: false } : user)),
        );
      } catch (e) {
        console.warn('Could not unmute audio:', e);
        message.warning('Could not unmute audio. Please check your microphone permissions.');
      }
    }
  };

  // Helper: Start local video and attach to container
  const startVideo = async (stream, userId) => {
    try {
      await stream.startVideo();
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      document.querySelector('video-player-container').appendChild(userVideo);
    } catch (e) {
      console.warn('Could not start video:', e);
      message.warning('Could not start video. Please check your camera permissions.');
      throw e; // Re-throw to let toggleVideo handle it
    }
  };

  // Helper: Start remote video and attach to container (chỉ attach khi user đang bật video, tránh lỗi 6001 "user is not send video")
  const startUserVideo = async (stream, userId, ref, isVideoOn) => {
    if (!stream || !userId || !ref) {
      return;
    }
    // Detach video trước nếu đã từng attach
    try {
      stream.detachVideo(userId);
    } catch (e) {
      // ignore nếu chưa từng attach
    }
    ref.innerHTML = '';
    if (!isVideoOn) {
      return;
    }
    try {
      let userVideo = await stream.attachVideo(userId, RESOLUTION);
      ref.appendChild(userVideo);
    } catch (e) {
      if (e && (e.errorCode === 6001 || (e.reason && e.reason.includes('not send video')))) {
        return;
      }
      console.warn(`Could not attach video for user ${userId}:`, e);
    }
  };

  // Helper: Stop local video and detach from container
  const stopVideo = async (stream, userId) => {
    try {
      await stream.stopVideo();
      stream.detachVideo(userId);
    } catch (e) {
      console.warn('Could not stop video:', e);
    }
  };

  // Bật/tắt video
  const toggleVideo = async () => {
    if (!client) return;
    const stream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;
    if (isVideoEnabled) {
      try {
        await stopVideo(stream, userId);
        setIsVideoEnabled(false);
      } catch (e) {
        console.warn('Could not stop video:', e);
        message.warning('Could not stop video. Please check your camera permissions.');
      }
    } else {
      try {
        await startVideo(stream, userId);
        setIsVideoEnabled(true);
      } catch (e) {
        console.warn('Could not start video:', e);
        // Don't show error message here as startVideo already shows it
      }
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    allUsers.forEach((user) => {
      if (!userVideoRefs.current[user.userId]) {
        return;
      }
      const isVideoOn = !!(user.isVideoOn || user.bVideoOn);
      startUserVideo(stream, user.userId, userVideoRefs.current[user.userId], isVideoOn);
    });
  }, [allUsers, client, pinnedUser]);

  // share screen
  const handleStartShare = async () => {
    if (!client) return;
    const currentUser = client.getCurrentUserInfo();
    setActiveShareUser(currentUser);

    const stream = client.getMediaStream();
    const userId = currentUser.userId;

    // Tự động tắt camera khi bật sharing
    // if (isVideoEnabled) {
    //   try {
    //     await stopVideo(stream, userId);
    //     setIsVideoEnabled(false);
    //   } catch (e) {
    //     console.warn('Could not stop video when starting share:', e);
    //   }
    // }

    try {
      if (stream.isStartShareScreenWithVideoElement()) {
        setShareRenderType('video');
        await stream.startShareScreen(shareVideoRef.current, { enableOptimizeForSharedVideo: true });
      } else {
        setShareRenderType('canvas');
        await stream.startShareScreen(shareCanvasRef.current, { enableOptimizeForSharedVideo: true });
      }
    } catch (e) {
      message.error(e.message || 'Failed to start screen share');
    }
  };

  // stop share screen
  const handleStopShare = async () => {
    if (!client) return;
    setActiveShareUser(null);
    setShareRenderType('canvas');
    const stream = client.getMediaStream();
    try {
      await stream.stopShareScreen();
    } catch (e) {
      message.error(e.message || 'Failed to stop screen share');
    }
  };

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();
    const onActiveShareChange = (payload) => {
      if (payload.state === 'Active') {
        const user = allUsers.find((u) => u.userId === payload.userId);
        setActiveShareUser(user || { userId: payload.userId });
        if (shareCanvasRef.current) {
          stream.startShareView(shareCanvasRef.current, payload.userId);
        }
      } else if (payload.state === 'Inactive') {
        setActiveShareUser(null);
        setShareRenderType('canvas');
        stream.stopShareView();
      }
    };
    client.on('active-share-change', onActiveShareChange);
    return () => {
      client.off('active-share-change', onActiveShareChange);
    };
  }, [client, allUsers]);

  const renderUserBox = (user, isMain = false) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? 'Unpin screen' : 'Pin screen'}
        </Menu.Item>
      </Menu>
    );
    return (
      <div key={user.userId} className={`session-video-user-box${isMain ? ' main' : ''}`}>
        <Dropdown overlay={menu} trigger={['click']}>
          <Button shape="circle" icon={<MoreOutlined />} size="small" className="session-video-user-menu" />
        </Dropdown>
        <div className="session-video-username">
          {user.displayName || user.userId}
          {user.userId === client?.getCurrentUserInfo()?.userId ? ' (You)' : ''}
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
                fontSize: 16,
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
        </div>
        {user.isVideoOn || user.bVideoOn ? (
          <video-player-container
            ref={(el) => (userVideoRefs.current[user.userId] = el)}
            className="session-video-player"
          ></video-player-container>
        ) : (
          <div className="session-video-avatar-icon">
            <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
          </div>
        )}
      </div>
    );
  };

  // Render user list item for the side panel
  const renderUserListItem = (user) => {
    const isPinned = pinnedUser && pinnedUser.userId === user.userId;
    const isCurrentUser = user.userId === client?.getCurrentUserInfo()?.userId;

    const menu = (
      <Menu>
        <Menu.Item key="pin" onClick={() => setPinnedUser(isPinned ? null : user)}>
          {isPinned ? 'Unpin screen' : 'Pin screen'}
        </Menu.Item>
      </Menu>
    );

    return (
      <div key={user.userId} className="user-list-item">
        <div className="user-item-name">
          <div className="user-item-avatar-icon">
            <UserOutlined style={{ fontSize: 16, color: '#333' }} />
          </div>
          {user.displayName || user.userId}
          {isCurrentUser && ' (You)'}
        </div>
        <div className="user-item-status">
          <span className="user-status-icon audio">
            <AudioOutlined
              style={{
                color: user.muted ? '#e53935' : '#4caf50',
              }}
            />
            {user.muted && <span className="icon-slash" />}
          </span>
          <span className="user-status-icon video">
            <VideoCameraOutlined
              style={{
                color: user.isVideoOn || user.bVideoOn ? '#4caf50' : '#e53935',
              }}
            />
            {!(user.isVideoOn || user.bVideoOn) && <span className="icon-slash" />}
          </span>
          {isPinned && (
            <span className="pin-indicator" style={{ color: '#1890ff', fontSize: 12 }}>
              📌
            </span>
          )}
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} size="small" className="user-item-menu-button" />
          </Dropdown>
        </div>
      </div>
    );
  };

  const mainContent = pinnedUser || activeShareUser;
  const currentUserId = client?.getCurrentUserInfo()?.userId;
  const isCurrentUserSharing = activeShareUser && activeShareUser.userId === currentUserId;
  const isAnyoneSharing = !!activeShareUser;
  const isOtherUserSharing = isAnyoneSharing && !isCurrentUserSharing;

  // Clear chat toast timeout on unmount
  useEffect(() => {
    return () => {
      if (chatToastTimeoutRef.current) {
        clearTimeout(chatToastTimeoutRef.current);
      }
    };
  }, []);

  // Listen for incoming Zoom chat messages
  useEffect(() => {
    if (!client) return;

    const onChatMessage = (payload) => {
      try {
        const currentUserIdInner = client.getCurrentUserInfo()?.userId;
        const senderInfo = payload?.sender || {};
        const text = payload?.message || payload?.content || '';
        if (!text) return;

        const isSelf = senderInfo.userId === currentUserIdInner;
        const senderName = isSelf
          ? 'You'
          : senderInfo.name || senderInfo.userId || senderInfo.userGuid || 'Unknown';

        setChatMessages((prev) => [
          ...prev,
          {
            id: payload.id || payload.msgId || Date.now(),
            sender: senderName,
            text,
            timestamp: payload.timestamp || new Date().toISOString(),
            isSelf,
          },
        ]);

        // Nếu panel chat đang đóng và đây là tin nhắn từ người khác -> show custom toast
        if (!isSelf && !showChat) {
          if (chatToastTimeoutRef.current) {
            clearTimeout(chatToastTimeoutRef.current);
          }
          setChatToast({
            sender: senderName,
            text,
          });
          chatToastTimeoutRef.current = setTimeout(() => {
            setChatToast(null);
          }, 4000);
        }
      } catch (e) {
        console.warn('Error handling chat-on-message payload:', e);
      }
    };

    try {
      client.on('chat-on-message', onChatMessage);
    } catch (e) {
      console.warn('Failed to bind chat-on-message listener on client:', e);
    }

    return () => {
      try {
        client.off('chat-on-message', onChatMessage);
      } catch (e) {
        // ignore
      }
    };
  }, [client, showChat]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const text = chatInput.trim();
    setChatInput('');

    if (chatClient) {
      try {
        // Send to everyone in the session
        await chatClient.sendToAll(text);
      } catch (e) {
        console.warn('Failed to send chat via Zoom:', e);
        message.error(e.message || 'Failed to send message');
      }
    }
  };

  /** Xử lý khi user chọn một trong 3 lựa chọn trên modal từ khóa nhạy cảm */
  const handleSensitiveModalChoice = async (type, note) => {
    if (!sensitiveModal || sensitiveModalSubmitting) return;
    const sessionUserId = sessionUserIdRef.current;
    if (!sessionUserId) {
      message.error('Session info not found.');
      return;
    }
    // Chọn "Dừng cuộc gọi" -> mở modal nhập feedback (flow giống ced-app)
    if (type === 'end_session') {
      pendingEndSessionRef.current = { ...sensitiveModal };
      setSensitiveModal(null);
      setSensitiveModalSubmitting(false);
      setEndSessionFeedbackVisible(true);
      setEndSessionFeedbackNote('');
      return;
    }

    setSensitiveModalSubmitting(true);
    const params = {
      session_user_id: sessionUserId,
      speakerName: sensitiveModal.speaker,
      keyword: sensitiveModal.keyword,
      category: sensitiveModal.category,
      type,
      transcript: sensitiveModal.transcript || '',
      source: sensitiveModal.source || 'json',
    };
    if (note !== undefined && note !== null) params.note = String(note).trim();
    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      await (promise || Promise.resolve(null));
    } catch (e) {
      console.warn('logSensitiveKeywordEvent error:', e);
      message.error('Failed to save your choice. Please try again.');
      setSensitiveModalSubmitting(false);
      return;
    }
    setSensitiveModal(null);
    setSensitiveModalSubmitting(false);

    // SAFE_CONFIRMED hoặc ACCIDENTAL_MENTION: tiếp tục phiên — bật lại audio và video
    if (client) {
      try {
        const stream = client.getMediaStream();
        const userId = client.getCurrentUserInfo()?.userId;
        if (stream) {
          await stream.unmuteAudio();
          setIsAudioEnabled(true);
          if (userId) {
            await startVideo(stream, userId);
            setIsVideoEnabled(true);
          }
        }
      } catch (e) {
        console.warn('Could not resume media after sensitive modal:', e);
        message.warning('Session resumed but you may need to turn your microphone/camera back on.');
      }
    }
  };

  /** Xác nhận dừng cuộc gọi từ modal feedback (sau khi user đã chọn "Dừng cuộc gọi" trên modal chính) */
  const handleEndSessionFeedbackConfirm = async () => {
    const pending = pendingEndSessionRef.current;
    if (!pending) {
      setEndSessionFeedbackVisible(false);
      return;
    }
    const sessionUserId = sessionUserIdRef.current;
    if (!sessionUserId) {
      message.error('Session info not found.');
      setEndSessionFeedbackVisible(false);
      pendingEndSessionRef.current = null;
      return;
    }
    setSensitiveModalSubmitting(true);
    const params = {
      session_user_id: sessionUserId,
      speakerName: pending.speaker,
      keyword: pending.keyword,
      category: pending.category,
      type: 'end_session',
      transcript: pending.transcript || '',
      source: pending.source || 'json',
    };
    const note = endSessionFeedbackNote.trim();
    if (note) params.note = note;
    try {
      const thunk = logSensitiveKeywordEventAction && logSensitiveKeywordEventAction(params);
      const promise = typeof thunk === 'function' ? thunk() : thunk;
      await (promise || Promise.resolve(null));
    } catch (e) {
      console.warn('logSensitiveKeywordEvent error:', e);
      message.error('Failed to save your choice. Please try again.');
      setSensitiveModalSubmitting(false);
      return;
    }
    pendingEndSessionRef.current = null;
    setEndSessionFeedbackVisible(false);
    setEndSessionFeedbackNote('');
    setSensitiveModalSubmitting(false);
    if (client) {
      await client.leave();
      clientRef.current = null;
      setClient(null);
    }
    history.push(`/session-detail/${sessionId}`);
  };

  const sensitiveModalOpen = !!sensitiveModal;

  return (
    <div className="session-video-wrapper">
      {/* Modal cảnh báo từ khóa nhạy cảm — nội dung và flow giống ced-app, style ced-web */}
      <Modal
        title={I18n.t('video_call_sensitive_warning_title')}
        visible={sensitiveModalOpen}
        closable={false}
        maskClosable={false}
        footer={null}
        width={560}
        className="sensitive-keyword-modal"
        wrapClassName="sensitive-keyword-modal-wrap"
        getContainer={() => document.body}
        zIndex={2147483647}
      >
        {sensitiveModal && (
          <>
            <div className="sensitive-keyword-modal-body">
              <p className="sensitive-keyword-modal-content">
                {I18n.t('video_call_sensitive_warning_content')}
              </p>
              <div className="sensitive-keyword-infographic">
                <strong>{I18n.t('video_call_sensitive_guide_link')}</strong>
                <ul>
                  <li>Do not share passwords, OTPs, or credit card details in the call.</li>
                  <li>Do not share personal identification numbers or sensitive data.</li>
                  <li>If in doubt, pause and confirm with your guide or end the session.</li>
                </ul>
              </div>
            </div>
            <div className="sensitive-keyword-modal-footer">
              <Button
                type="primary"
                block
                className="sensitive-keyword-btn safe"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('SAFE_CONFIRMED')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_safe')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_safe_subtitle')}</span>
              </Button>
              <Button
                block
                className="sensitive-keyword-btn accidental"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('ACCIDENTAL_MENTION')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_continue_careful')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_continue_careful_subtitle')}</span>
              </Button>
              <Button
                danger
                block
                className="sensitive-keyword-btn end"
                loading={sensitiveModalSubmitting}
                onClick={() => handleSensitiveModalChoice('end_session')}
              >
                <span className="sensitive-keyword-btn-title">{I18n.t('video_call_sensitive_action_stop_call')}</span>
                <span className="sensitive-keyword-btn-subtitle">{I18n.t('video_call_sensitive_action_stop_call_subtitle')}</span>
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* Modal xác nhận dừng cuộc gọi + nhập feedback (flow như ced-app) */}
      <Modal
        title={I18n.t('sensitive_session_negative_dialog_title')}
        visible={endSessionFeedbackVisible}
        closable={true}
        maskClosable={false}
        footer={null}
        width={400}
        className="sensitive-keyword-end-feedback-modal"
        wrapClassName="sensitive-keyword-modal-wrap"
        getContainer={() => document.body}
        zIndex={2147483646}
        onCancel={() => {
          setEndSessionFeedbackVisible(false);
          setEndSessionFeedbackNote('');
          pendingEndSessionRef.current = null;
        }}
      >
        <p className="sensitive-keyword-feedback-description">
          {I18n.t('sensitive_session_negative_dialog_description')}
        </p>
        <Input.TextArea
          placeholder={I18n.t('sensitive_session_negative_dialog_feedback_hint')}
          value={endSessionFeedbackNote}
          onChange={(e) => setEndSessionFeedbackNote(e.target.value)}
          rows={3}
          className="sensitive-keyword-feedback-input"
        />
        <div className="sensitive-keyword-feedback-footer">
          <Button
            onClick={() => {
              setEndSessionFeedbackVisible(false);
              setEndSessionFeedbackNote('');
              pendingEndSessionRef.current = null;
            }}
          >
            {I18n.t('cancel')}
          </Button>
          <Button
            type="primary"
            danger
            loading={sensitiveModalSubmitting}
            onClick={handleEndSessionFeedbackConfirm}
          >
            {I18n.t('sensitive_session_negative_dialog_end_call_button')}
          </Button>
        </div>
      </Modal>

      <div className="session-video-main-layout">
        <div className={`session-video-main-section ${mainContent ? 'has-main' : 'no-main'}`}>
          {pinnedUser &&
            (() => {
              const fullPinnedUser = allUsers.find((user) => user.userId === pinnedUser.userId);
              return fullPinnedUser ? renderUserBox(fullPinnedUser, true) : null;
            })()}
          <div
            className={`session-video-share-container ${pinnedUser ? 'has-pin' : 'no-pin'} ${
              activeShareUser ? 'has-share' : 'no-share'
            }`}
          >
            <canvas
              ref={shareCanvasRef}
              id="users-screen-share-content-canvas"
              width={1920}
              height={1080}
              className={`session-video-share-canvas ${shareRenderType === 'canvas' && activeShareUser ? ' show' : ''}`}
            />
            <video
              ref={shareVideoRef}
              id="my-screen-share-content-video"
              width={960}
              height={540}
              className={`session-video-share-video ${shareRenderType === 'video' && activeShareUser ? ' show' : ''}`}
              autoPlay
              playsInline
              muted
            />
          </div>
        </div>
        <div className={`session-video-side-section ${mainContent ? 'has-main' : 'no-main'}`}>
          <div
            className={`${mainContent ? 'session-video-sidebar' : 'session-video-users-list'} ${
              pinnedUser ? 'has-pin' : 'no-pin'
            } ${activeShareUser ? 'has-share' : 'no-share'}`}
          >
            {allUsers.filter((u) => !pinnedUser || u.userId !== pinnedUser.userId).map((user) => renderUserBox(user))}
          </div>
        </div>
      </div>

      {/* User List Panel */}
      {showUserList && (
        <div className="user-list-panel">
          <div className="user-list-header">
            <h5>Participants ({allUsers.length})</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowUserList(false)}
              className="close-button"
            />
          </div>
          {allUsers.map((user) => renderUserListItem(user))}
        </div>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="chat-panel">
          <div className="chat-header">
            <h5>Metting Chat</h5>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setShowChat(false)}
              className="close-button"
            />
          </div>
          <div className="chat-messages">
            {chatMessages.length === 0 && (
              <div className="chat-empty">No messages yet. Start the conversation!</div>
            )}
            {chatMessages.map((item, index) => {
              const prev = chatMessages[index - 1];
              const showSender = !prev || prev.sender !== item.sender || prev.isSelf !== item.isSelf;

              return (
                <div
                  key={item.id}
                  className={`chat-message ${item.isSelf ? 'self' : 'other'}`}
                >
                  {showSender && (
                    <div className="chat-message-meta">
                      <span className="chat-sender">{item.sender}</span>
                    </div>
                  )}
                  <div className="chat-message-text">{item.text}</div>
                </div>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <Input.TextArea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type a message..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
            />
            <Button type="primary" onClick={handleSendChat} disabled={!chatInput.trim()} icon={<SendOutlined />} />
          </div>
        </div>
      )}

      <div className="session-video-controls-bar">
        <Tooltip title={isAudioEnabled ? 'Mute Audio' : 'Unmute Audio'}>
          <Button
            onClick={toggleAudio}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <AudioOutlined />
                {!isAudioEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn audio-toggle-btn"
          />
        </Tooltip>
        <Tooltip title={isVideoEnabled ? 'Stop Video' : 'Start Video'}>
          <Button
            onClick={toggleVideo}
            style={{ marginRight: 8 }}
            icon={
              <span className="media-toggle-icon">
                <VideoCameraOutlined />
                {!isVideoEnabled && <span className="media-toggle-slash" />}
              </span>
            }
            className="media-toggle-btn video-toggle-btn"
          />
        </Tooltip>
        {!isAnyoneSharing && (
          <Tooltip title="Share Screen">
            <Button onClick={handleStartShare} style={{ marginRight: 8 }} icon={<ShareAltOutlined />} />
          </Tooltip>
        )}
        {isCurrentUserSharing && (
          <Tooltip title="Stop Sharing">
            <Button onClick={handleStopShare} style={{ marginRight: 8 }} icon={<StopOutlined />} danger />
          </Tooltip>
        )}
        {isOtherUserSharing && (
          <Tooltip title="Screen sharing in progress" placement="top">
            <Button style={{ marginRight: 8 }} icon={<ShareAltOutlined />} disabled />
          </Tooltip>
        )}
        <Tooltip title="Show Participants">
          <Button
            onClick={() => {
              const next = !showUserList;
              setShowUserList(next);
              if (next) setShowChat(false);
            }}
            style={{ marginRight: 8 }}
            icon={<UserOutlined />}
            type={showUserList ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title={showChat ? 'Close Chat' : 'Open Chat'}>
          <Button
            onClick={() => {
              const next = !showChat;
              setShowChat(next);
              if (next) setShowUserList(false);
            }}
            style={{ marginRight: 8 }}
            icon={<MessageOutlined />}
            type={showChat ? 'primary' : 'default'}
          />
        </Tooltip>
        <Tooltip title="Leave Session">
          <Button danger onClick={leaveSession} icon={<PhoneOutlined />} />
        </Tooltip>
      </div>
      {chatToast && (
        <div
          className="chat-toast"
          onClick={() => {
            setShowChat(true);
            setShowUserList(false);
            setChatToast(null);
          }}
        >
          <div className="chat-toast-sender">{chatToast.sender}</div>
          <div className="chat-toast-text">{chatToast.text}</div>
        </div>
      )}
      {loading && (
        <div className="session-video-loading-overlay">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = {
  getDetailSession,
  getTokenSession,
  fetchUserMvp,
  verifyJoinSession,
  moderateText,
  logSensitiveKeywordEvent,
};
export default connect(mapStateToProps, mapDispatchToProps)(SessionVideoCustom);
